import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type { Department, Severity } from './taskQueries.ts';
import type { RecordKind } from './tasks.ts';

/**
 * Held deadlines, the release act and triage (146, Code 2's routes; the shapes as Code 0 relayed them, 23 October).
 * Nothing in a URL: the reads that take a department take it in the body. Code 2's answers (23 October): GET /tasks/held
 * carries may_release (the release act; since A-#149 also may_release_all and may_release_in, the departments the
 * reader may release) and may_triage (the departments the reader may triage), which the screen uses
 * instead of any rule of its own; "held" is exactly what a release would make, net of everything else, and an overdue
 * row's held is always 0 (overdue goes by triage only).
 */

export type Band = 'within_14' | 'later' | 'overdue';
export const BAND_WORDS: Record<Band, string> = {
  within_14: 'Due within 14 days',
  later: 'Due later',
  overdue: 'Overdue (triage only)',
};

/** One summary row: a department and band, how many are held, and why the rest are withheld. */
export interface HeldRow {
  department: Department;
  band: Band;
  held: number;
  triage_only: number;
  withheld_hidden: number;
  withheld_exempt_visit: number;
}

/**
 * One row to triage. why 'death_no_date' (146's F2): a final report and account whose deadline cannot be raised
 * because the date of death is not recorded; deadline_id and due_date are null, and it is NEVER released: it says
 * "record the date of death" and points to the client's record.
 */
export interface TriageRow {
  deadline_id: string | null;
  client_id: string;
  client_ref: string;
  display_name: string;
  label: string;
  department: Department;
  due_date: string | null;
  days_overdue: number | null;
  severity: Severity;
  is_statutory: boolean;
  why: 'overdue' | 'death' | 'death_no_date';
  /** A-#145 (b): who a band release would give it to, the client's Primary in the department, or null (its queue). */
  default_to: string | null;
  /**
   * P-31 (A-#286): what records the deadline, the server's deadline_recorded_by() (null: its task completes it, or no
   * record shows it done), and whether THIS reader may record it here, by that record's own route's gates. Absent on
   * the deaths with no date (no deadline).
   */
  rule_code?: string | null;
  record_kind?: RecordKind | null;
  court_order_id?: string | null;
  property_id?: string | null;
  source_known?: boolean | null;
  may_record?: boolean;
}

/** P-31 (A-#286 Q4 A): a rule with no completion source; may_record_source is tiers 1-2, for a rule a record shows done. */
export interface UnsourcedRule {
  rule_code: string;
  label: string;
  record_kind: RecordKind | null;
  may_record_source: boolean;
}

const json = (body: unknown): RequestInit => ({
  method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body),
});

/** Tiers 1-2 see every department; a queue owner, their own. */
export function useHeld(enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'held'],
    // may_release: the release page at all; may_release_all: every department at once (tiers 1-2); may_release_in:
    // the departments one by one (tiers 1-2 all, a director their own, A-#149).
    queryFn: () => apiFetch<{ rows: HeldRow[]; may_release: boolean; may_release_all: boolean; may_release_in: Department[];
      may_triage: Department[]; unsourced?: UnsourcedRule[] }>('/tasks/held', getToken),
    enabled,
    ...queryDefaults,
  });
}

/**
 * A department's triage rows, read by a POST so nothing travels in the address. Since 156 (A-#246) `rows` are the
 * department's work only; `deaths_no_date` is the deaths with no date recorded, the same whatever department is asked,
 * counted for this reader.
 */
/** 167 (P-24 26): a client the commission tracker covers with no acting order on file (triage_commission_no_order()). */
export interface CommissionNoOrderRow { client_id: string; client_ref: string; display_name: string; label: string; why: string }

export function useTriage(department: Department | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'triage', department],
    queryFn: () => apiFetch<{ rows: TriageRow[]; deaths_no_date: { count: number; rows: TriageRow[] };
                              commission_no_order?: { shown: boolean; count: number; rows: CommissionNoOrderRow[] } }>(
      '/tasks/triage', getToken, json({ department })),
    enabled: department !== null,
    ...queryDefaults,
  });
}

/**
 * D-127: the deaths with no date recorded, for the Department queue page, where Legal sees them. The server decides:
 * shown is true for tiers 1-2 and a role in Legal (may_see_legal_deaths(), 163); anyone else is answered shown false
 * with no rows. The rows are Triage's own block. A POST so nothing travels in the address.
 */
export function useQueueDeaths() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['tasks', 'queue-deaths'],
    queryFn: () => apiFetch<{ shown: boolean; count: number; rows: TriageRow[] }>('/tasks/queue/deaths-no-date', getToken, json({})),
    ...queryDefaults,
  });
}

/** One death certificate on file, found by its filename (D-116). The date is the day Clio holds it from, in New York. */
export interface DeathCertificate { copy_id: string; filename: string; date: string | null; status: string }
export interface DeathCertificates {
  holds_documents: boolean;
  clients: { client_id: string; certificates: DeathCertificate[] }[];
}

/**
 * D-116: the death certificates on file for the undated deaths this reader sees, ASKED ONLY WHEN THE READER PRESSES
 * Show (a mutation, never a query on load): each client's list is an audited Documents look, so it is never fetched
 * without someone asking. A reader without the Documents module is answered holds_documents false.
 */
export function useDeathCertificates() {
  const { getToken } = useSession();
  return useMutation({
    mutationFn: () => apiFetch<DeathCertificates>('/tasks/triage/death-certificates', getToken, json({})),
  });
}

function useHeldWrite<I, O>(call: (input: I, getToken: () => Promise<string>) => Promise<O>) {
  const { getToken } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: I) => call(input, getToken),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tasks'] }); },
  });
}

/** Ruling 5's release act: tiers 1-2 only; a whole band, for one department or all (null). Overdue is refused. */
export const useReleaseHeld = () =>
  useHeldWrite<{ department: Department | null; band: 'within_14' | 'later'; reason: string }, { released: number }>(
    (input, t) => apiFetch('/tasks/held/release', t, json(input)));

/**
 * P-31 (A-#286 Q4 A): "This rule can now be recorded", with a required reason: 146's set_rule_completion_source(),
 * tiers 1-2. The rule's withheld deadlines then count as held or triage, like any other.
 */
export const useRecordCompletionSource = () =>
  useHeldWrite<{ rule_code: string; reason: string }, { recorded: boolean }>(
    (input, t) => apiFetch('/deadline-rules/completion-source', t, json(input)));

/** Triage, per selection: to a person, or to the department's queue (null). Closing goes through the deadline's own paths. */
export const useReleaseTriage = () =>
  useHeldWrite<{ deadlineIds: string[]; toStaffId: string | null; reason: string }, { released: number }>(
    (input, t) => apiFetch('/tasks/triage/release', t, json(input)));
